import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import './RolesManagement.css';

const RolesManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  const { isAdmin, user: currentUser } = useAuth();
  const navigate = useNavigate();

  // Role definitions
  const roleDefinitions = {
    Administrateur: {
      name: 'Administrateur',
      description: 'Accès complet à toutes les fonctionnalités',
      permissions: [
        'Gestion des utilisateurs',
        'Gestion des rôles',
        'Gestion des paramètres',
        'Gestion des réunions',
        'Statistiques complètes',
        'Mode maintenance'
      ],
      color: '#667eea'
    },
    Presentateur: {
      name: 'Présentateur',
      description: 'Peut organiser et animer des réunions',
      permissions: [
        'Créer des réunions',
        'Inviter des participants',
        'Gérer les réunions',
        'Enregistrer des réunions'
      ],
      color: '#f5576c'
    },
    moderator: {
      name: 'Modérateur',
      description: 'Peut modérer les réunions',
      permissions: [
        'Voir toutes les réunions',
        'Modérer les réunions',
        'Gérer les participants'
      ],
      color: '#4facfe'
    },
    user: {
      name: 'Utilisateur',
      description: 'Accès de base à la plateforme',
      permissions: [
        'Participer aux réunions',
        'Voir son profil',
        'Modifier son profil'
      ],
      color: '#a8edea'
    }
  };

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/admin');
      return;
    }
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminAPI.updateUserRole(userId, newRole);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleBulkRoleChange = async () => {
    if (selectedUsers.length === 0 || !bulkAction) return;
    
    if (!window.confirm(`Voulez-vous attribuer le rôle "${roleDefinitions[bulkAction]?.name}" à ${selectedUsers.length} utilisateur(s) ?`)) {
      return;
    }

    try {
      for (const userId of selectedUsers) {
        await adminAPI.updateUserRole(userId, bulkAction);
      }
      await loadUsers();
      setSelectedUsers([]);
      setBulkAction('');
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  // Filter users by role
  const filteredUsers = selectedRole === 'all' 
    ? users 
    : users.filter(u => u.role === selectedRole);

  // Role statistics
  const roleStats = {
    Administrateur: users.filter(u => u.role === 'Administrateur').length,
    Presentateur: users.filter(u => u.role === 'Presentateur').length,
    moderator: users.filter(u => u.role === 'moderator').length,
    user: users.filter(u => u.role === 'user').length,
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="roles-page">
      <div className="roles-header">
        <h1>Gestion des Rôles</h1>
        <p>Attribuez et gérez les rôles des utilisateurs</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Role Cards */}
      <div className="role-cards">
        {Object.entries(roleDefinitions).map(([key, role]) => (
          <div 
            className={`role-card ${selectedRole === key ? 'selected' : ''}`}
            key={key}
            onClick={() => setSelectedRole(selectedRole === key ? 'all' : key)}
            style={{ borderColor: role.color }}
          >
            <div className="role-header" style={{ background: role.color }}>
              <h3>{role.name}</h3>
            </div>
            <div className="role-body">
              <p className="role-description">{role.description}</p>
              <div className="role-count">
                <span className="count">{roleStats[key]}</span>
                <span className="label">utilisateur{roleStats[key] !== 1 ? 's' : ''}</span>
              </div>
              <ul className="role-permissions">
                {role.permissions.slice(0, 3).map((perm, index) => (
                  <li key={index}>{perm}</li>
                ))}
                {role.permissions.length > 3 && (
                  <li className="more">+{role.permissions.length - 3} autres</li>
                )}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Bulk Actions */}
      {selectedRole !== 'all' && (
        <div className="bulk-actions">
          <div className="selection-info">
            <span>{selectedUsers.length} utilisateur(s) sélectionné(s)</span>
            {selectedUsers.length > 0 && (
              <button 
                className="btn-clear"
                onClick={() => setSelectedUsers([])}
              >
                Tout désélectionner
              </button>
            )}
          </div>
          <div className="bulk-controls">
            <select 
              value={bulkAction} 
              onChange={(e) => setBulkAction(e.target.value)}
              disabled={selectedUsers.length === 0}
            >
              <option value="">Sélectionner une action</option>
              {Object.entries(roleDefinitions).map(([key, role]) => (
                <option key={key} value={key}>
                 Attribuer le rôle: {role.name}
                </option>
              ))}
            </select>
            <button 
              className="btn-primary"
              onClick={handleBulkRoleChange}
              disabled={!bulkAction || selectedUsers.length === 0}
            >
              Appliquer
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="users-section">
        <h2>
          Utilisateurs 
          {selectedRole !== 'all' && (
            <span className="filter-badge" style={{ background: roleDefinitions[selectedRole]?.color }}>
              {roleDefinitions[selectedRole]?.name}
            </span>
          )}
        </h2>
        
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input 
                    type="checkbox"
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Utilisateur</th>
                <th>Email</th>
                <th>Rôle actuel</th>
                <th>Statut</th>
                <th>Dernière connexion</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id} className={selectedUsers.includes(user.id) ? 'selected' : ''}>
                  <td className="checkbox-col">
                    <input 
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                    />
                  </td>
                  <td>
                    <div className="user-cell">
                      <div 
                        className="user-avatar"
                        style={{ background: roleDefinitions[user.role]?.color || '#ccc' }}
                      >
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="user-info">
                        <span className="user-name">{user.name}</span>
                        {user.id === currentUser?.id && (
                          <span className="you-badge">Vous</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      className="role-select"
                      style={{ 
                        borderColor: roleDefinitions[user.role]?.color,
                        color: roleDefinitions[user.role]?.color
                      }}
                    >
                      {Object.entries(roleDefinitions).map(([key, role]) => (
                        <option key={key} value={key}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                      {user.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td>
                    {user.lastLogin 
                      ? new Date(user.lastLogin).toLocaleDateString('fr-FR')
                      : 'Jamais'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {Object.entries(roleDefinitions).map(([key, role]) => (
                        <button
                          key={key}
                          className={`btn-icon ${user.role === key ? 'active-role' : ''}`}
                          onClick={() => handleRoleChange(user.id, key)}
                          title={`Attribuer: ${role.name}`}
                          style={{ 
                            borderColor: role.color,
                            background: user.role === key ? role.color : 'transparent',
                            color: user.role === key ? 'white' : role.color
                          }}
                        >
                          {key.charAt(0)}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <div className="no-data">Aucun utilisateur avec ce rôle</div>
          )}
        </div>
      </div>

      {/* Role Permission Details Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Détails des Rôles</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {Object.entries(roleDefinitions).map(([key, role]) => (
                <div key={key} className="role-detail">
                  <h3 style={{ color: role.color }}>{role.name}</h3>
                  <p>{role.description}</p>
                  <ul>
                    {role.permissions.map((perm, index) => (
                      <li key={index}>{perm}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesManagement;
